/**
 * Web Push 구독 헬퍼.
 *
 * 영구지침: docs/develop/pwa/pwa-spec.md §7.1
 */
import {
  fetchVapidPublicKey,
  subscribePush,
  unsubscribePush,
} from '@/features/pwa/api'
import type { PushSubscriptionResponse } from '@/features/pwa/types'

/** 푸시 지원 여부 */
export function isPushSupported(): boolean {
  return (
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  )
}

/** 현재 권한 상태 */
export function getNotificationPermission(): NotificationPermission {
  if (typeof Notification === 'undefined') return 'denied'
  return Notification.permission
}

/** 권한 요청 */
export async function requestNotificationPermission(): Promise<NotificationPermission> {
  if (typeof Notification === 'undefined') return 'denied'
  return Notification.requestPermission()
}

/**
 * VAPID 키 → SW 구독 → 백엔드 등록 까지 일괄 처리.
 *
 * 권한이 없으면 자동 요청한다.
 */
export async function subscribeToPush(): Promise<PushSubscriptionResponse> {
  if (!isPushSupported()) {
    throw new Error('이 브라우저는 Web Push를 지원하지 않습니다')
  }

  // 1. 권한 확인 + 요청
  let permission = getNotificationPermission()
  if (permission === 'default') {
    permission = await requestNotificationPermission()
  }
  if (permission !== 'granted') {
    throw new Error('알림 권한이 거부되었습니다')
  }

  // 2. SW 준비 대기
  const registration = await navigator.serviceWorker.ready

  // 3. VAPID 공개키 조회
  const publicKey = await fetchVapidPublicKey()
  if (!publicKey) {
    throw new Error('서버 푸시 기능이 비활성화되어 있습니다')
  }

  // 4. 브라우저 푸시 구독 생성
  const existing = await registration.pushManager.getSubscription()
  if (existing) {
    // 이미 구독 중 → 백엔드에 다시 등록 (재방문 동기화)
    return registerSubscriptionWithBackend(existing)
  }

  const subscription = await registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(publicKey),
  })

  // 5. 백엔드 등록
  return registerSubscriptionWithBackend(subscription)
}

/**
 * 푸시 구독 해지 (브라우저 + 백엔드).
 */
export async function unsubscribeFromPush(subscriptionId: number): Promise<void> {
  if (!isPushSupported()) return

  const registration = await navigator.serviceWorker.ready
  const subscription = await registration.pushManager.getSubscription()
  if (subscription) {
    await subscription.unsubscribe()
  }
  await unsubscribePush(subscriptionId)
}

// ─────────────────────────────────────────────────────────────────────────────
// 내부 유틸
// ─────────────────────────────────────────────────────────────────────────────

async function registerSubscriptionWithBackend(
  subscription: PushSubscription
): Promise<PushSubscriptionResponse> {
  const p256dhKey = subscription.getKey('p256dh')
  const authKey = subscription.getKey('auth')

  if (!p256dhKey || !authKey) {
    throw new Error('구독 키를 가져올 수 없습니다')
  }

  return subscribePush({
    endpoint: subscription.endpoint,
    keys: {
      p256dh: arrayBufferToBase64Url(p256dhKey),
      auth: arrayBufferToBase64Url(authKey),
    },
    userAgent: navigator.userAgent,
  })
}

/** Base64URL → Uint8Array (VAPID applicationServerKey) */
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/')
  const rawData = atob(base64)
  const outputArray = new Uint8Array(rawData.length)
  for (let i = 0; i < rawData.length; i++) {
    outputArray[i] = rawData.charCodeAt(i)
  }
  return outputArray
}

/** ArrayBuffer → Base64URL (구독 키 직렬화) */
function arrayBufferToBase64Url(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer)
  let binary = ''
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]!)
  }
  const base64 = btoa(binary)
  // URL-safe base64
  return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}
